import { ExtendedMarkerData, ExtendedPolylineData, TriggerType } from '@bringg-frontend/bringg-map';
import { Connection, Driver, Task, TaskStatus, Team } from '@bringg/types';
import { flatMap as _flatMap } from 'lodash';
import { decodePath, EtaCalculationUpdateResults } from '@bringg-frontend/eta-calculator';

import { getActionableWaypoint } from 'bringg-web/stores/tasks/task-utils';
import { iconsContent } from './icons';
import { driverTooltipProvider } from 'bringg-web/features/monitoring/shared/components/tooltips/driver/driver-tooltip';
import { iconsProvider } from './icons-provider';
import { taskTooltipProvider } from 'bringg-web/features/monitoring/shared/components/tooltips/task/task-tooltip';

export const generateTaskMarker = (task: Task, _, tasks: Task[], color = 'blue'): ExtendedMarkerData => {
	const actionableWaypoint = getActionableWaypoint(task);
	const { lat, lng } = actionableWaypoint;

	return {
		id: task.id,
		groupId: task.run_id,
		text: task.status === TaskStatus.Cancelled || task.priority === task.id ? '-' : `${task.priority}`, // task.priority === task.id as default, it will be set to a real priority after running the optimization
		color: color,
		zIndex: task.priority === task.id ? 1 : tasks.length + 1 - task.priority, // display top priority on top
		opacity: task.status === TaskStatus.Cancelled || task.status === TaskStatus.Done ? 0.6 : 1,
		shouldIncludeInBounds: true,
		location: {
			lat,
			lng
		},
		tooltip: taskTooltipProvider(task),
		tooltipOptions: {
			trigger: TriggerType.CLICK
		}
	};
};

export const generateDriverMarker = (driver: Driver, color: string): ExtendedMarkerData => {
	const { id, active_shift_id } = driver;

	const isOnShift = !!active_shift_id;
	const driverLocation = driver?.lat && driver?.lng && { lat: driver.lat, lng: driver.lng };

	if (!isOnShift || !driverLocation) {
		return;
	}

	return {
		plainSvg:
			driver.status === Connection.Online ? iconsProvider.getDriverByColor(color) : iconsProvider.offlineDriver,
		id: `${id}_${color}`,
		location: driverLocation,
		tooltip: driverTooltipProvider(driver),
		tooltipOptions: {
			trigger: TriggerType.CLICK
		},
		shouldIncludeInBounds: true,
		zIndex: 100
	};
};

export const generateTeamMarker = (team: Team): ExtendedMarkerData => {
	const { id, lat, lng, name } = team;
	if (!lat || !lng) {
		return;
	}
	return {
		title: name,
		id,
		location: { lat, lng },
		icon: iconsContent.warehouse,
		tooltip: name,
		tooltipOptions: {
			trigger: TriggerType.CLICK
		},
		shouldIncludeInBounds: true
	};
};

export const generatePolyline = (
	etaCalculationsResult: EtaCalculationUpdateResults[],
	color = 'blue'
): ExtendedPolylineData[] => {
	const id = etaCalculationsResult[0].updatedRoutesInfo[0].run_id;
	return etaCalculationsResult[0].updatedRunsDirections.map(({ directions }) => ({
		id,
		path: _flatMap(directions, directionAsString => decodePath(directionAsString)) || [],
		color,
		strokeOpacity: 0.5,
		onMouseover: (polyline: google.maps.Polyline) => {
			polyline.setOptions({ strokeOpacity: 1 });
		},
		onMouseout: (polyline: google.maps.Polyline) => {
			polyline.setOptions({ strokeOpacity: 0.5 });
		}
	})) as ExtendedPolylineData[];
};
